import { useParams, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import CustomerFinanceTab from '../../../CustomerFinanceTab';
import CustomerJobsTab from '../../../CustomerJobsTab';
import CustomerSitesTab from '../../../CustomerSitesTab';
import CustomerContractsTab from '../../../CustomerContractsTab';
import AddJobModal from '../../../AddJobModal';
import SiteDetailModern from '../../../SiteDetailModern';
import { apiFetch } from '../../../utils/api';

function CustomerDetailModern() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [customerTypes, setCustomerTypes] = useState([]);
  const [contactRoles, setContactRoles] = useState([]);
  const [siteTypes, setSiteTypes] = useState([]);
  const [editedCustomer, setEditedCustomer] = useState(null);
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContact, setNewContact] = useState({
    contact_name: '',
    contact_role: 'manager',
    phone: '',
    mobile: '',
    email: '',
    notes: ''
  });
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [showEditSiteModal, setShowEditSiteModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [newSite, setNewSite] = useState({
    site_name: '',
    address: '',
    city: '',
    county: '',
    postcode: '',
    site_type: 'site',
    contact_name: '',
    phone: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomer();
    fetchSites();
    fetchContacts();
    fetchCustomerTypes();
    fetchContactRoles();
    fetchSiteTypes();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const res = await apiFetch(`/api/customers/${id}`);
      const data = await res.json();
      setCustomer(data);
      setEditedCustomer(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching customer:', err);
      setLoading(false);
    }
  };

  const fetchCustomerTypes = async () => {
    try {
      const res = await apiFetch('/api/custom-lists?list_type=customer_type');
      const data = await res.json();
      setCustomerTypes(data);
    } catch (err) {
      console.error('Error fetching customer types:', err);
    }
  };

  const fetchContactRoles = async () => {
    try {
      const res = await apiFetch('/api/custom-lists?list_type=contact_role');
      const data = await res.json();
      setContactRoles(data);
    } catch (err) {
      console.error('Error fetching contact roles:', err);
    }
  };

  const fetchSiteTypes = async () => {
    try {
      const res = await apiFetch('/api/custom-lists?list_type=site_type');
      const data = await res.json();
      setSiteTypes(data);
    } catch (err) {
      console.error('Error fetching site types:', err);
    }
  };

  const fetchSites = async () => {
    try {
      const res = await apiFetch(`/api/sites?customer_id=${id}`);
      const data = await res.json();
      setSites(data);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await apiFetch(`/api/customers/${id}/contacts`);
      const data = await res.json();
      setAdditionalContacts(data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  };

  const saveContact = async (contact) => {
    try {
      const res = await apiFetch(`/api/customers/${id}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contact)
      });
      const savedContact = await res.json();
      setAdditionalContacts([...additionalContacts, savedContact]);
      return savedContact;
    } catch (err) {
      console.error('Error saving contact:', err);
      alert('Failed to save contact');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await apiFetch(`/api/contacts/${contactId}`, {
        method: 'DELETE'
      });
      setAdditionalContacts(additionalContacts.filter(c => c.id !== contactId));
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Failed to delete contact');
    }
  };

  const saveSite = async (site) => {
    try {
      const res = await apiFetch(`/api/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...site, customer_id: id })
      });
      const savedSite = await res.json();
      setSites([...sites, savedSite]);
      return savedSite;
    } catch (err) {
      console.error('Error saving site:', err);
      alert('Failed to save site');
    }
  };

  const updateSite = async (siteId, updates) => {
    try {
      const res = await apiFetch(`/api/sites/${siteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const updatedSite = await res.json();
      setSites(sites.map(s => s.id === siteId ? updatedSite : s));
      return updatedSite;
    } catch (err) {
      console.error('Error updating site:', err);
      alert('Failed to update site');
    }
  };

  const deleteSite = async (siteId) => {
    try {
      await apiFetch(`/api/sites/${siteId}`, {
        method: 'DELETE'
      });
      setSites(sites.filter(s => s.id !== siteId));
    } catch (err) {
      console.error('Error deleting site:', err);
      alert('Failed to delete site');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Customer Not Found</h2>
          <button onClick={() => navigate("/customers")} className="text-blue-600 hover:text-blue-800">
            ← Back to Customers
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'sites', label: 'Sites', icon: '📍', count: sites.length },
    { id: 'contacts', label: 'Contacts', icon: '👥' },
    { id: 'equipment', label: 'Site Equipment', icon: '🚪' },
    { id: 'contracts', label: 'PPM Contracts', icon: '📋' },
    { id: 'jobs', label: 'Jobs', icon: '🔧' },
    { id: 'quotes', label: 'Quotes', icon: '📄' },
    { id: 'invoices', label: 'Invoices', icon: '💳' },
    { id: 'finance', label: 'Financial', icon: '💰' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/customers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedCustomer)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setCustomer(updated);
        setEditedCustomer(updated);
        alert('Customer updated successfully!');
      } else {
        alert('Failed to update customer');
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      alert('Error saving customer');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/customers")}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">{customer.company_name}</h1>
              <span className="text-sm text-blue-100">{customer.customer_type || 'Customer'}</span>
            </div>
            <button 
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
            >
              {saving ? '⏳ Saving...' : '💾 Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-full px-4 py-3">
        <div className="bg-gray-50 rounded-xl shadow-sm p-2 border border-gray-200 inline-flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              {tab.label}
              {tab.count !== null && tab.count !== undefined && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-3">
        <div className="rounded-xl bg-gray-100 border-4 border-gray-400 shadow-[inset_6px_6px_12px_rgba(0,0,0,0.3),inset_-4px_-4px_8px_rgba(255,255,255,0.9)] overflow-auto max-h-[calc(100vh-135px)] min-h-[650px]">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Customer Information</h2>
                <button 
                  onClick={() => setShowAddJobModal(true)}
                  className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium transition"
                >
                  ➕ Add a Job
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Company Name</label>
                    <input
                      type="text"
                      value={editedCustomer?.company_name || ''}
                      onChange={(e) => setEditedCustomer({...editedCustomer, company_name: e.target.value})}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="field-label mb-2 block">Contact Person</label>
                    <input
                      type="text"
                      value={editedCustomer?.contact_name || ''}
                      onChange={(e) => setEditedCustomer({...editedCustomer, contact_name: e.target.value})}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="field-label mb-1.5 block">Customer Type</label>
                    <select 
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editedCustomer?.customer_type || 'commercial'}
                      onChange={(e) => {
                        setEditedCustomer({...editedCustomer, customer_type: e.target.value});
                      }}
                    >
                      {customerTypes.map(type => (
                        <option key={type.id} value={type.list_value}>
                          {type.list_value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Address</label>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Address Line 1"
                        value={editedCustomer?.address_line1 || ''}
                        onChange={(e) => setEditedCustomer({...editedCustomer, address_line1: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Address Line 2"
                        value={editedCustomer?.address_line2 || ''}
                        onChange={(e) => setEditedCustomer({...editedCustomer, address_line2: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="City"
                        value={editedCustomer?.city || ''}
                        onChange={(e) => setEditedCustomer({...editedCustomer, city: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="County"
                        value={editedCustomer?.county || ''}
                        onChange={(e) => setEditedCustomer({...editedCustomer, county: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Postcode"
                        value={editedCustomer?.postcode || ''}
                        onChange={(e) => setEditedCustomer({...editedCustomer, postcode: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Contact Details</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-600">Phone</label>
                        <input
                          type="tel"
                          placeholder="Phone number"
                          value={editedCustomer?.phone || ''}
                          onChange={(e) => setEditedCustomer({...editedCustomer, phone: e.target.value})}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Mobile</label>
                        <input
                          type="tel"
                          placeholder="Mobile number"
                          value={editedCustomer?.mobile || ''}
                          onChange={(e) => setEditedCustomer({...editedCustomer, mobile: e.target.value})}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">Email</label>
                        <input
                          type="email"
                          placeholder="Email address"
                          value={editedCustomer?.email || ''}
                          onChange={(e) => setEditedCustomer({...editedCustomer, email: e.target.value})}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          )}

          {/* Sites Tab */}
          {activeTab === 'sites' && (
            <CustomerSitesTab
              customerId={id}
              sites={sites}
              onAddSite={() => setShowAddSiteModal(true)}
              onEditSite={(site) => {
                setSelectedSite(site);
                setShowEditSiteModal(true);
              }}
              onDeleteSite={deleteSite}
              onRefresh={fetchSites}
            />
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Contacts</h2>
                <button 
                  onClick={() => setShowAddContactModal(true)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition shadow-sm"
                >
                  ➕ Add Contact
                </button>
              </div>
              
              {/* Primary Contact Card */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs text-blue-600 font-semibold mb-1">PRIMARY CONTACT</div>
                    <h3 className="text-lg font-semibold text-gray-900">{customer.contact_name || 'Not set'}</h3>
                  </div>
                  <span className="px-3 py-1 bg-gray-300 text-black rounded-lg text-xs font-semibold">
                    PRIMARY
                  </span>
                </div>
                
                <div className="space-y-2 text-gray-900">
                  {customer.phone && (
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-600">📞</span>
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  {customer.mobile && (
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-600">📱</span>
                      <span>{customer.mobile}</span>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center space-x-2">
                      <span className="text-blue-600">📧</span>
                      <span>{customer.email}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Additional Contacts */}
              {additionalContacts.length > 0 && (
                <div className="space-y-3">
                  {additionalContacts.map((contact, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{contact.contact_name}</h3>
                          <div className="text-xs text-gray-500 font-semibold uppercase mt-1">{contact.contact_role}</div>
                        </div>
                        <button 
                          onClick={() => {
                            if (window.confirm(`Remove ${contact.contact_name}?`)) {
                              deleteContact(contact.id);
                            }
                          }}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          🗑️ Remove
                        </button>
                      </div>
                      
                      <div className="space-y-2 text-gray-900">
                        {contact.phone && (
                          <div className="flex items-center space-x-2">
                            <span className="text-blue-600">📞</span>
                            <span>{contact.phone}</span>
                          </div>
                        )}
                        {contact.mobile && (
                          <div className="flex items-center space-x-2">
                            <span className="text-blue-600">📱</span>
                            <span>{contact.mobile}</span>
                          </div>
                        )}
                        {contact.email && (
                          <div className="flex items-center space-x-2">
                            <span className="text-blue-600">📧</span>
                            <span>{contact.email}</span>
                          </div>
                        )}
                        {contact.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-sm text-gray-600">{contact.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {additionalContacts.length === 0 && (
                <p className="text-gray-500 text-sm">Additional contacts will appear here once added.</p>
              )}
            </div>
          )}

          {activeTab === 'equipment' && (
            <div className="p-8 text-center py-12">
              <div className="text-6xl mb-4">🚪</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Site Equipment</h3>
              <p className="text-gray-500">Doors, gates, and other equipment coming soon</p>
            </div>
          )}

          {activeTab === 'contracts' && (
            <CustomerContractsTab customerId={customer.id} customerName={customer.company_name} />
          )}

          {activeTab === 'jobs' && (
            <CustomerJobsTab customerId={customer.id} customerName={customer.company_name} />
          )}

          {activeTab === 'quotes' && (
            <div className="p-8 text-center py-12">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quotes</h3>
              <p className="text-gray-500">Coming soon</p>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="p-8 text-center py-12">
              <div className="text-6xl mb-4">💳</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Invoices</h3>
              <p className="text-gray-500">Coming soon</p>
            </div>
          )}

          {activeTab === 'finance' && (
            <div className="p-8">
              <CustomerFinanceTab customerId={id} />
            </div>
          )}
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-white">Add Contact</h2>
                <button 
                  onClick={() => setShowAddContactModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Name */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Contact Name *</label>
                  <input
                    type="text"
                    value={newContact.contact_name}
                    onChange={(e) => setNewContact({...newContact, contact_name: e.target.value})}
                    placeholder="e.g., John Smith"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Contact Role */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Role / Position *</label>
                  <select
                    value={newContact.contact_role}
                    onChange={(e) => setNewContact({...newContact, contact_role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {contactRoles.map(role => (
                      <option key={role.id} value={role.list_value}>
                        {role.list_value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Phone */}
                <div>
                  <label className="field-label mb-2 block">Phone</label>
                  <input
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    placeholder="01234 567890"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Mobile */}
                <div>
                  <label className="field-label mb-2 block">Mobile</label>
                  <input
                    type="tel"
                    value={newContact.mobile}
                    onChange={(e) => setNewContact({...newContact, mobile: e.target.value})}
                    placeholder="07123 456789"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Email */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Email</label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                    placeholder="john.smith@example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Notes</label>
                  <textarea
                    value={newContact.notes}
                    onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                    placeholder="Additional information about this contact..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex items-center justify-between">
              <p className="text-sm text-gray-600">* Required fields</p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowAddContactModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (newContact.contact_name && newContact.contact_role) {
                      await saveContact(newContact);
                      setNewContact({
                        contact_name: '',
                        contact_role: 'manager',
                        phone: '',
                        mobile: '',
                        email: '',
                        notes: ''
                      });
                      setShowAddContactModal(false);
                    } else {
                      alert('Please fill in Contact Name and Role');
                    }
                  }}
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-semibold transition shadow-sm"
                >
                  💾 Save Contact
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Site Modal */}
      {showAddSiteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-white">Add Site</h2>
                <button 
                  onClick={() => setShowAddSiteModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Site Name */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Site Name *</label>
                  <input
                    type="text"
                    value={newSite.site_name}
                    onChange={(e) => setNewSite({...newSite, site_name: e.target.value})}
                    placeholder="e.g., Main Warehouse, Branch Office"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Site Type */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Site Type *</label>
                  <select
                    value={newSite.site_type}
                    onChange={(e) => setNewSite({...newSite, site_type: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    {siteTypes.map(type => (
                      <option key={type.id} value={type.list_value}>
                        {type.list_value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Address</label>
                  <input
                    type="text"
                    value={newSite.address}
                    onChange={(e) => setNewSite({...newSite, address: e.target.value})}
                    placeholder="Street address"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="field-label mb-2 block">City</label>
                  <input
                    type="text"
                    value={newSite.city}
                    onChange={(e) => setNewSite({...newSite, city: e.target.value})}
                    placeholder="City"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* County */}
                <div>
                  <label className="field-label mb-2 block">County</label>
                  <input
                    type="text"
                    value={newSite.county}
                    onChange={(e) => setNewSite({...newSite, county: e.target.value})}
                    placeholder="County"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Postcode */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Postcode</label>
                  <input
                    type="text"
                    value={newSite.postcode}
                    onChange={(e) => setNewSite({...newSite, postcode: e.target.value})}
                    placeholder="e.g., SW1A 1AA"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Site Contact */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Site Contact Name</label>
                  <input
                    type="text"
                    value={newSite.contact_name}
                    onChange={(e) => setNewSite({...newSite, contact_name: e.target.value})}
                    placeholder="On-site contact person"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="field-label mb-2 block">Phone</label>
                  <input
                    type="tel"
                    value={newSite.phone}
                    onChange={(e) => setNewSite({...newSite, phone: e.target.value})}
                    placeholder="01234 567890"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="field-label mb-2 block">Email</label>
                  <input
                    type="email"
                    value={newSite.email}
                    onChange={(e) => setNewSite({...newSite, email: e.target.value})}
                    placeholder="site@example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Notes</label>
                  <textarea
                    value={newSite.notes}
                    onChange={(e) => setNewSite({...newSite, notes: e.target.value})}
                    placeholder="Access instructions, special requirements, etc."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex items-center justify-between">
              <p className="text-sm text-gray-600">* Required fields</p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowAddSiteModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (newSite.site_name && newSite.site_type) {
                      await saveSite(newSite);
                      setNewSite({
                        site_name: '',
                        address: '',
                        city: '',
                        county: '',
                        postcode: '',
                        site_type: 'site',
                        contact_name: '',
                        phone: '',
                        email: '',
                        notes: ''
                      });
                      setShowAddSiteModal(false);
                    } else {
                      alert('Please fill in Site Name and Site Type');
                    }
                  }}
                  className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-semibold transition shadow-sm"
                >
                  💾 Save Site
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Site Modal */}
      {showEditSiteModal && selectedSite && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-white">Edit Site</h2>
                <button 
                  onClick={() => {
                    setShowEditSiteModal(false);
                    setSelectedSite(null);
                  }}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Site Name */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Site Name *</label>
                  <input
                    type="text"
                    value={selectedSite.site_name || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, site_name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Site Type */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Site Type *</label>
                  <select
                    value={selectedSite.site_type || 'site'}
                    onChange={(e) => setSelectedSite({...selectedSite, site_type: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    {siteTypes.map(type => (
                      <option key={type.id} value={type.list_value}>
                        {type.list_value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Address</label>
                  <input
                    type="text"
                    value={selectedSite.address || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="field-label mb-2 block">City</label>
                  <input
                    type="text"
                    value={selectedSite.city || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, city: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* County */}
                <div>
                  <label className="field-label mb-2 block">County</label>
                  <input
                    type="text"
                    value={selectedSite.county || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, county: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Postcode */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Postcode</label>
                  <input
                    type="text"
                    value={selectedSite.postcode || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, postcode: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Site Contact */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Site Contact Name</label>
                  <input
                    type="text"
                    value={selectedSite.contact_name || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, contact_name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="field-label mb-2 block">Phone</label>
                  <input
                    type="tel"
                    value={selectedSite.phone || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="field-label mb-2 block">Email</label>
                  <input
                    type="email"
                    value={selectedSite.email || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="field-label mb-2 block">Notes</label>
                  <textarea
                    value={selectedSite.notes || ''}
                    onChange={(e) => setSelectedSite({...selectedSite, notes: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditSiteModal(false);
                  setSelectedSite(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedSite.site_name && selectedSite.site_type) {
                    await updateSite(selectedSite.id, selectedSite);
                    setShowEditSiteModal(false);
                    setSelectedSite(null);
                  } else {
                    alert('Please fill in Site Name and Site Type');
                  }
                }}
                className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-semibold transition shadow-sm"
              >
                💾 Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Job Modal */}
      {showAddJobModal && customer && (
        <AddJobModal
          customerId={customer.id}
          customerName={customer.company_name}
          onClose={() => setShowAddJobModal(false)}
          onJobCreated={() => setShowAddJobModal(false)}
        />
      )}
    </div>
  );
}

export default CustomerDetailModern;
